/**
 * Lenavio Learn — "My Training" employee surface.
 *
 * Lists every training assignment for the caller's employee row,
 * grouped into "to do" and "completed" sections. Clicking
 * "Start" opens the course content URL in a new tab; "Mark
 * complete" flips the assignment status (and writes a completion
 * audit row server-side).
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Clock,
  ExternalLink,
  GraduationCap,
} from "lucide-react";

import {
  completeAssignment,
  listMyCourses,
  type TrainingAssignment,
} from "@/api/training";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

export function MyTrainingPage() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<TrainingAssignment[] | null>(null);

  const refresh = async () => {
    try {
      setRows(await listMyCourses(true));
    } catch {
      addToast("error", "Could not load your training.");
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const todo = rows?.filter((r) => r.status !== "completed") ?? [];
  const done = rows?.filter((r) => r.status === "completed") ?? [];

  const handleComplete = async (a: TrainingAssignment) => {
    try {
      await completeAssignment(a.id, {});
      addToast("success", "Marked complete!");
      void refresh();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Could not mark complete.";
      addToast("error", detail);
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="My Training"
        subtitle="Assigned courses and certifications."
      />

      {rows === null ? (
        <div className="grid grid-cols-1 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 rounded bg-border/30 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* To do */}
          <h2 className="text-xs uppercase tracking-wide text-text-muted mb-2">
            To do ({todo.length})
          </h2>
          {todo.length === 0 ? (
            <Card className="text-center py-8 mb-6">
              <CheckCircle2 className="h-8 w-8 mx-auto text-success mb-2" />
              <p className="text-sm font-medium text-text-primary">
                You're all caught up
              </p>
              <p className="text-xs text-text-muted">
                We'll let you know when something new is assigned.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-3 mb-6">
              {todo.map((a) => (
                <AssignmentRow
                  key={a.id}
                  assignment={a}
                  onComplete={() => handleComplete(a)}
                />
              ))}
            </div>
          )}

          {/* Completed */}
          {done.length > 0 && (
            <>
              <h2 className="text-xs uppercase tracking-wide text-text-muted mb-2">
                Completed ({done.length})
              </h2>
              <div className="grid grid-cols-1 gap-3">
                {done.map((a) => (
                  <AssignmentRow key={a.id} assignment={a} onComplete={null} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </motion.div>
  );
}


function AssignmentRow({
  assignment,
  onComplete,
}: {
  assignment: TrainingAssignment;
  onComplete: (() => void) | null;
}) {
  const overdue = assignment.status === "overdue";
  const completed = assignment.status === "completed";

  return (
    <Card
      className={
        overdue
          ? "border-danger/40 bg-danger/5"
          : completed
            ? "opacity-80"
            : undefined
      }
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div
            className={
              "rounded-lg p-2 mt-0.5 " +
              (overdue
                ? "bg-danger/10 text-danger"
                : completed
                  ? "bg-success/10 text-success"
                  : "bg-accent/10 text-accent")
            }
          >
            {completed ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : (
              <GraduationCap className="h-5 w-5" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-base font-semibold font-heading text-text-primary truncate">
                {assignment.course_title || "Untitled course"}
              </h3>
              {assignment.course_category && (
                <Badge variant="info">{assignment.course_category}</Badge>
              )}
              {overdue && <Badge variant="danger">Overdue</Badge>}
              {completed && <Badge variant="success">Done</Badge>}
            </div>
            <div className="flex items-center gap-3 text-xs text-text-muted">
              {assignment.course_duration_minutes && (
                <span>{assignment.course_duration_minutes} min</span>
              )}
              {assignment.due_date && (
                <>
                  {assignment.course_duration_minutes && <span>·</span>}
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Due {assignment.due_date}
                  </span>
                </>
              )}
              {assignment.completed_at && (
                <>
                  <span>·</span>
                  <span>Completed {assignment.completed_at.slice(0, 10)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          {assignment.course_content_url && !completed && (
            <a
              href={assignment.course_content_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-primary hover:border-accent/60"
            >
              Open
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {onComplete && !completed && (
            <Button size="sm" onClick={onComplete}>
              Mark complete
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
